// WCAG 2.x contrast for the themes studio: a warning, never a block. Pure.
import { luminance, rgbOfHex } from '@/lib/colour'

/** Contrast ratio of two `#rrggbb[aa]` colours (1..21); undefined when either is not hex. */
export const contrastRatio = (a: string, b: string): number | undefined => {
  const la = luminance(a.slice(0, 7))
  const lb = luminance(b.slice(0, 7))
  if (la === undefined || lb === undefined) return undefined
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

export interface ContrastCheck {
  /** a short key for the pair, stable for the UI ("text-background") */
  id: string
  label: string
  fg: string
  bg: string
  ratio: number
  need: number
  pass: boolean
}

/** The colours of one mode that matter, as the variables the Shell sets (hex only). */
export interface ContrastInput {
  background: string
  raised: string
  text: string
  muted: string
  faint: string
  accent: string
  onAccent: string
  ok?: string
  warn?: string
  danger?: string
  info?: string
  highlight?: string
}

const PAIRS: { id: string; label: string; fg: keyof ContrastInput; bg: keyof ContrastInput; need: number }[] =
  [
    { id: 'text-background', label: 'Text on the background', fg: 'text', bg: 'background', need: 4.5 },
    { id: 'text-raised', label: 'Text on cards', fg: 'text', bg: 'raised', need: 4.5 },
    {
      id: 'muted-background',
      label: 'Muted text on the background',
      fg: 'muted',
      bg: 'background',
      need: 4.5,
    },
    { id: 'faint-background', label: 'Faint text on the background', fg: 'faint', bg: 'background', need: 3 },
    { id: 'accent-background', label: 'Accent on the background', fg: 'accent', bg: 'background', need: 3 },
    { id: 'onaccent-accent', label: 'Text on the accent', fg: 'onAccent', bg: 'accent', need: 4.5 },
    {
      id: 'highlight-background',
      label: 'Highlight on the background',
      fg: 'highlight',
      bg: 'background',
      need: 3,
    },
    { id: 'ok-background', label: 'Ok on the background', fg: 'ok', bg: 'background', need: 3 },
    { id: 'warn-background', label: 'Warn on the background', fg: 'warn', bg: 'background', need: 3 },
    { id: 'danger-background', label: 'Danger on the background', fg: 'danger', bg: 'background', need: 3 },
    { id: 'info-background', label: 'Info on the background', fg: 'info', bg: 'background', need: 3 },
  ]

/** Every pair that can be measured, passing or not. Pairs with a missing or non-hex colour are left out. */
export const contrastChecks = (c: ContrastInput): ContrastCheck[] => {
  const out: ContrastCheck[] = []
  for (const p of PAIRS) {
    const fg = c[p.fg]
    const bg = c[p.bg]
    if (!fg || !bg || !rgbOfHex(fg) || !rgbOfHex(bg)) continue
    const ratio = contrastRatio(fg, bg)
    if (ratio === undefined) continue
    out.push({ id: p.id, label: p.label, fg, bg, ratio, need: p.need, pass: ratio >= p.need })
  }
  return out
}

/** Only the pairs that fall short: what the studio shows as "Low contrast". */
export const contrastWarnings = (c: ContrastInput): ContrastCheck[] =>
  contrastChecks(c).filter((x) => !x.pass)

/** A nearby colour that clears `need` against `bg`: `fg` moved toward white or black, whichever helps. */
export const fixContrast = (fg: string, bg: string, need: number): string => {
  const f = rgbOfHex(fg)
  const b = luminance(bg.slice(0, 7))
  if (!f || b === undefined) return fg
  const toward = b > 0.4 ? 0 : 255
  const hex = (t: number) =>
    '#' +
    f
      .map((v) =>
        Math.round(v + (toward - v) * t)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  for (let t = 0; t <= 1.0001; t += 0.05) {
    const c = hex(Math.min(t, 1))
    if ((contrastRatio(c, bg) ?? 0) >= need) return c
  }
  return hex(1)
}

/** The measurable colours of a draw's variables (themeSpecToVars), for contrastChecks. */
export const contrastInputOf = (vars: Record<string, string>): ContrastInput | undefined => {
  const hex = (k: string) => {
    const v = vars[k]
    return v && rgbOfHex(v) ? v : undefined
  }
  const background = hex('--color-ink')
  const raised = hex('--color-panel')
  const text = hex('--color-fg')
  const muted = hex('--color-muted')
  const faint = hex('--color-faint')
  const accent = hex('--color-accent')
  const onAccent = hex('--color-on-accent')
  if (!background || !raised || !text || !muted || !faint || !accent || !onAccent) return undefined
  return {
    background,
    raised,
    text,
    muted,
    faint,
    accent,
    onAccent,
    ok: hex('--color-ok'),
    warn: hex('--color-warn'),
    danger: hex('--color-danger'),
    info: hex('--color-info'),
    highlight: hex('--color-highlight'),
  }
}
